import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { admins, verifiedContentUsers } from "../../Constants";
import { getAllDonators, getDonationAmount } from "../../services/DonatorService";
import { useBadges, isTopDonator as isTopDonatorBadge } from "../../services/BadgeService";
import { Donator } from "../../models/Donator";
import { TeamTier } from "../../models/TeamTier";
import { useTeamTiers, getTeamTiers, getTeamTierForSteamId } from "../../services/TeamTierService";
import Loading from "../../components/Loading";
import {
    Analytics, ArrowForward, BarChart, Check, CheckCircle, CloudUpload, DoneAll, EmojiEvents,
    Favorite, FilterAlt, Groups, Layers, Lock, LockOpen, Map, MilitaryTech, Movie, Payments,
    PersonSearch, QueryStats, Science, TrendingUp, Upgrade, Verified, Visibility,
    VolunteerActivism, WorkspacePremium
} from "@mui/icons-material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material";
import styles from "./DonatorPage.module.css";

const MOBILEPAY_URL = 'https://qr.mobilepay.dk/box/9229be2b-bcf7-4f26-acfc-e43ed97e7a5d/pay-in';

// The team tier is a parallel product, not a rung on the personal kr ladder — it never appears in
// TIERS or the comparison matrix, both of which are keyed on the donated amount.
const TEAM_TIER_PRICE = 350;

// The team tier is not a kr level, so it needs its own column identity in the comparison matrix.
const TEAM_COL = -1;

type MuiIcon = OverridableComponent<SvgIconTypeMap<{}, "svg">>;

interface FeatureDef {
    key: string;
    link: string;
    unlock: number;
    free?: boolean;
    icon: MuiIcon;
    iconBg: string;
    iconColor: string;
    bulletIcons: MuiIcon[];
}

const FEATURES: FeatureDef[] = [
    {
        key: 'analyse', link: '/analyse', unlock: 25, free: true,
        icon: Analytics, iconBg: 'rgba(241,92,37,0.15)', iconColor: '#f15c25',
        bulletIcons: [Layers, Map, Visibility, LockOpen],
    },
    {
        key: 'scout', link: '/scout', unlock: 25,
        icon: PersonSearch, iconBg: 'rgba(74,163,255,0.15)', iconColor: '#4aa3ff',
        bulletIcons: [FilterAlt, QueryStats, Groups],
    },
    {
        key: 'upload', link: '/my-demos', unlock: 100,
        icon: CloudUpload, iconBg: 'rgba(39,174,96,0.15)', iconColor: '#4ade80',
        bulletIcons: [BarChart, Movie, Analytics, Lock],
    },
];

interface PerkDef {
    icon: MuiIcon;
    color?: string;
    textColor?: string;
}

interface TierDef {
    key: string;
    value: number;
    amount: string;
    unit: string;
    amountColor: string;
    icon: MuiIcon;
    iconBg: string;
    iconColor: string;
    baseBorder: string;
    baseShadow: string;
    featured?: boolean;
    perks: PerkDef[];
}

const GREEN = '#27ae60';
const GOLD = '#f1c40f';

// The Top Donator tier isn't bought — it's the hand-assigned `top_donator` badge, so there's no
// price to compare against. This sentinel stands in for its "value" wherever tiers match by amount.
const TOP_TIER = Number.MAX_SAFE_INTEGER;

const TIERS: TierDef[] = [
    {
        key: 'donator', value: 25, amount: '25', unit: 'kr.+', amountColor: '#4ade80',
        icon: VolunteerActivism, iconBg: 'rgba(39,174,96,0.15)', iconColor: '#4ade80',
        baseBorder: '1px solid #333', baseShadow: 'none',
        perks: [
            { icon: Verified },
            { icon: TrendingUp },
            { icon: Analytics, color: '#ff9933', textColor: '#fff' },
            { icon: PersonSearch },
        ],
    },
    {
        key: 'tier100', value: 100, amount: '100', unit: 'kr.+', amountColor: '#4ecbe0',
        icon: CloudUpload, iconBg: 'rgba(23,162,184,0.18)', iconColor: '#4ecbe0',
        baseBorder: '1px solid #2a6b78', baseShadow: 'none',
        perks: [
            { icon: DoneAll },
            { icon: CloudUpload, color: '#4ecbe0', textColor: '#fff' },
            { icon: Movie, color: '#4ecbe0' },
        ],
    },
    {
        key: 'premium', value: 200, amount: '200', unit: 'kr.+', amountColor: '#ff9933', featured: true,
        icon: WorkspacePremium, iconBg: 'rgba(241,92,37,0.18)', iconColor: '#f15c25',
        baseBorder: '2px solid #f15c25', baseShadow: '0 0 18px rgba(241,92,37,0.22)',
        perks: [
            { icon: DoneAll },
            { icon: Verified, color: '#4aa3ff' },
            { icon: Movie, color: '#ff9933', textColor: '#fff' },
            { icon: Science, color: '#ff9933', textColor: '#fff' },
        ],
    },
    {
        key: 'top', value: TOP_TIER, amount: '★', unit: '', amountColor: GOLD,
        icon: WorkspacePremium, iconBg: 'rgba(241,196,15,0.16)', iconColor: GOLD,
        baseBorder: '1px solid #7a6414', baseShadow: 'none',
        perks: [
            { icon: DoneAll },
            { icon: WorkspacePremium, color: '#f1c40f' },
            { icon: MilitaryTech, color: '#f1c40f' },
            { icon: EmojiEvents, color: '#f1c40f' },
        ],
    },
];

// Comparison matrix: yes / no / text cells per column (Donator / 100+ / Premium / Top / Hold).
// The Hold column is not a kr level — it is the team tier, which grants the tooling rows and
// none of the personal ones.
type CompareCell = { type: 'yes' } | { type: 'no' } | { type: 'text'; value: string; color: string };
const Y: CompareCell = { type: 'yes' };
const N: CompareCell = { type: 'no' };
const T = (value: string, color: string): CompareCell => ({ type: 'text', value, color });

const COL_VALUES = [25, 100, 200, TOP_TIER, TEAM_COL];
const TEAM_ORANGE = '#ff9933';
const COMPARE_CELLS: CompareCell[][] = [
    // Donator-badge — personal, so the team tier does not grant it
    [Y, Y, Y, Y, N],
    // Top placement on LFT/LFP — the team tier boosts LFP ads only
    [Y, Y, Y, Y, T('LFP', TEAM_ORANGE)],
    // Analyse (up to 2 games)
    [Y, Y, Y, Y, Y],
    // Unlimited analyse (>2 games)
    [Y, Y, Y, Y, Y],
    // Player Scout
    [Y, Y, Y, Y, Y],
    // Private demo uploads
    [N, Y, Y, Y, Y],
    // Highlight clips per match — the team tier adds no personal clip bonus
    [T('1', '#e2e8f0'), T('2', '#4ecbe0'), T('3', '#ff9933'), T('3', '#f1c40f'), T('1', '#e2e8f0')],
    // Blue premium badge
    [N, N, Y, Y, N],
    // Beta access
    [N, N, Y, Y, N],
    // Gold crown
    [N, N, N, Y, N],
    // Team donator badge — the one row only the team tier fills
    [N, N, N, N, Y],
];

// The redesigned Premium page (Premium.dc.html). The whole feature is pre-launch
// (nav item beta-gated), so the redesign replaces the page in place.
const DonatorPage: React.FC = () => {
    const { user, authLoaded } = useContext(AuthContext);
    const { t } = useTranslation("pages");
    const [donators, setDonators] = useState<Donator[] | null>(null);
    const badges = useBadges();
    const teamTiers = useTeamTiers();
    const [freshTier, setFreshTier] = useState<TeamTier | null | undefined>(undefined);

    // This is the page people open to check what they have, so it must not answer from a cache that
    // predates the tier being granted. Force a fetch and prefer its answer over the session's.
    useEffect(() => {
        if (!user?.steamID) { setFreshTier(null); return; }
        getTeamTiers(true)
            .then((tiers) => setFreshTier(getTeamTierForSteamId(user.steamID, tiers)))
            .catch(() => setFreshTier(null));
    }, [user?.steamID]);

    const coveringTeam = (freshTier !== undefined ? freshTier : user?.teamTier) ?? null;

    useEffect(() => {
        getAllDonators().then(setDonators).catch(() => setDonators([]));
    }, []);

    // Donation level in kr. — from aggregated donations, bumped by admin-set flags
    const level = useMemo(() => {
        if (!user) return 0;
        let lvl = donators ? getDonationAmount(user.steamID, donators) : 0;
        if (user.premium) lvl = Math.max(lvl, 200);
        else if (user.donator) lvl = Math.max(lvl, 25);
        if (admins.includes(user.steamID) || verifiedContentUsers.includes(user.steamID)) lvl = Math.max(lvl, 200);
        return lvl;
    }, [user, donators]);

    if (!authLoaded) return <div className="text-center" style={{ paddingTop: 60 }}><Loading /></div>;

    // Rank #1 isn't derivable from the donated amount — it's the hand-assigned top_donator badge,
    // the same source the profile crown uses. Without it the king was shown as plain Premium and
    // asked to "become no. 1" on the tier he already holds.
    const isKing = !!user && !!badges && isTopDonatorBadge(user.steamID, badges);

    // A covered player with no personal donation would otherwise be told they have nothing, on a
    // page where every one of their perks is in fact unlocked.
    const teamOnly = !!coveringTeam && level < 25 && !isKing;
    const statusKey = isKing ? 'statusTop' : level >= 200 ? 'status200' : level >= 100 ? 'status100' : level >= 25 ? 'status25' : teamOnly ? 'statusTeam' : 'statusNone';
    const statusClass = isKing ? styles.statusTop : level >= 200 ? styles.status200 : level >= 100 ? styles.status100 : level >= 25 ? styles.status25 : teamOnly ? styles.status100 : styles.statusNone;
    const StatusIcon = isKing ? EmojiEvents : level >= 200 ? WorkspacePremium : (level >= 25 || teamOnly) ? CheckCircle : Lock;

    const paidValues = [25, 100, 200];
    const currentTier = isKing ? TOP_TIER : (paidValues.filter((v) => v <= level).pop() ?? 0);
    const hasTier = currentTier > 0;

    // One place decides which comparison column is "yours": a kr column when it matches the paid
    // tier, the Hold column when a team tier covers you. Both can light up at once.
    const isCurrentCol = (colValue: number) =>
        colValue === TEAM_COL ? !!coveringTeam : (colValue === currentTier && hasTier);

    const ctaVariant = isKing ? 'Top' : level >= 200 ? 'Premium' : level > 0 ? 'Upgrade' : 'None';
    const CtaIcon = ctaVariant === 'Upgrade' ? Upgrade : ctaVariant === 'Top' ? EmojiEvents : Favorite;

    return (
        <div className={styles.page}>
            {/* ===== Hero ===== */}
            <div className={styles.hero}>
                <span className={styles.heroPill}>
                    <WorkspacePremium sx={{ fontSize: 15 }} />
                    {t('premium.pill')}
                </span>
                <h1 className={styles.heroTitle}>{t('premium.heroTitle')}</h1>
                <p className={styles.heroDesc}>{t('premium.heroDesc')}</p>
            </div>

            {/* ===== Status banner ===== */}
            <div className={styles.statusWrap}>
                <div className={`${styles.status} ${statusClass}`}>
                    <StatusIcon sx={{ fontSize: 18 }} />
                    {t(`premium.${statusKey}`, { team: coveringTeam?.teamName ?? '' })}
                </div>
            </div>

            {/* ===== Feature gateway ===== */}
            <div className={styles.featureGrid}>
                {FEATURES.map((f) => {
                    const Icon = f.icon;
                    // Every feature card here (analyse, scout, upload) is part of the team tier, so
                    // being covered opens all three even at a personal level of 0.
                    const open = level >= f.unlock || !!coveringTeam;
                    const usable = open || !!f.free;
                    const name = t(`premium.features.${f.key}.name`);
                    const bullets = t(`premium.features.${f.key}.bullets`, { returnObjects: true }) as string[];

                    let tagClass: string, tagIcon: MuiIcon, tagText: string;
                    if (f.free && !open) {
                        tagClass = styles.tagFree; tagIcon = LockOpen; tagText = t('premium.tagFreeCap');
                    } else if (f.free && open) {
                        tagClass = styles.tagOpen; tagIcon = CheckCircle; tagText = t('premium.tagUnlimited');
                    } else if (open) {
                        tagClass = styles.tagOpen; tagIcon = CheckCircle; tagText = t('premium.tagUnlocked');
                    } else {
                        tagClass = styles.tagLocked; tagIcon = Lock; tagText = t('premium.tagFrom', { amount: f.unlock });
                    }
                    const TagIcon = tagIcon;

                    const cardClass = `${styles.featureCard} ${open ? styles.featureCardOpen : ''} ${!usable ? styles.featureCardLocked : ''}`;
                    const inner = (
                        <>
                            <div className={styles.featureTop}>
                                <span className={styles.featureIcon} style={{ background: f.iconBg, color: f.iconColor }}>
                                    <Icon sx={{ fontSize: 28 }} />
                                </span>
                                <span className={`${styles.featureTag} ${tagClass}`}>
                                    <TagIcon sx={{ fontSize: 13 }} />
                                    {tagText}
                                </span>
                            </div>
                            <div className={styles.featureName}>{name}</div>
                            <p className={styles.featureDesc}>{t(`premium.features.${f.key}.desc`)}</p>
                            <div className={styles.featureBullets}>
                                {(Array.isArray(bullets) ? bullets : []).map((text, i) => {
                                    const BulletIcon = f.bulletIcons[i] ?? CheckCircle;
                                    return (
                                        <div className={styles.featureBullet} key={text}>
                                            <BulletIcon sx={{ fontSize: 16, color: f.iconColor, marginTop: '1px', flexShrink: 0 }} />
                                            <span className={styles.featureBulletText}>{text}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <span
                                className={styles.featureCta}
                                style={{ color: usable ? (open ? '#4ade80' : '#ff9933') : 'rgba(255,255,255,0.45)' }}
                            >
                                {usable ? t('premium.openFeature', { name }) : t('premium.unlockFrom', { amount: f.unlock })}
                                {usable ? <ArrowForward sx={{ fontSize: 18 }} /> : <Lock sx={{ fontSize: 18 }} />}
                            </span>
                        </>
                    );

                    return usable ? (
                        <Link to={f.link} className={cardClass} key={f.key}>{inner}</Link>
                    ) : (
                        <a href="#stoet" className={cardClass} key={f.key}>{inner}</a>
                    );
                })}
            </div>

            {/* ===== Tiers ===== */}
            <h2 className={styles.sectionTitle}>{t('premium.tiersTitle')}</h2>
            <p className={styles.sectionDesc}>{t('premium.tiersDesc')}</p>

            <div className={styles.tierGrid}>
                {TIERS.map((tier) => {
                    const TierIcon = tier.icon;
                    const isTop = tier.value === TOP_TIER;
                    const isCurrent = tier.value === currentTier && hasTier;
                    // The king holds the throne, so every paid tier below it reads as "included".
                    const owned = !isCurrent && (level >= tier.value || (isKing && !isTop));
                    const perkTexts = t(`premium.perks.${tier.key}`, { returnObjects: true }) as string[];

                    const border = isCurrent ? (isTop ? `2px solid ${GOLD}` : '2px solid #27ae60') : tier.baseBorder;
                    const shadow = isCurrent
                        ? (isTop ? '0 0 18px rgba(241,196,15,0.25)' : '0 0 18px rgba(39,174,96,0.2)')
                        : tier.baseShadow;

                    let cta: React.ReactNode;
                    if (isCurrent) {
                        const currentStyle = isTop
                            ? { background: 'rgba(241,196,15,0.15)', color: GOLD, border: '1px solid rgba(241,196,15,0.4)' }
                            : { background: 'rgba(39,174,96,0.15)', color: '#4ade80', border: '1px solid rgba(39,174,96,0.4)' };
                        cta = (
                            <div className={styles.tierCta} style={currentStyle}>
                                {isTop ? <EmojiEvents sx={{ fontSize: 17 }} /> : <CheckCircle sx={{ fontSize: 17 }} />}
                                {t('premium.currentLevel')}
                            </div>
                        );
                    } else if (owned) {
                        cta = (
                            <div className={styles.tierCta} style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <Check sx={{ fontSize: 17 }} />
                                {t('premium.included')}
                            </div>
                        );
                    } else {
                        const label = isTop
                            ? t('premium.becomeTop')
                            : tier.featured
                                ? (level > 0 ? t('premium.upgrade') : t('premium.becomePremium'))
                                : (level > 0 ? t('premium.upgrade') : t('premium.donateAmount', { amount: tier.amount }));
                        const style = isTop
                            ? { background: 'rgba(241,196,15,0.12)', color: '#f1c40f', border: '1px solid rgba(241,196,15,0.3)' }
                            : tier.featured
                                ? { background: '#f15c25', color: '#fff', border: '1px solid transparent' }
                                : { background: 'rgba(255,255,255,0.05)', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.15)' };
                        cta = (
                            <a href={MOBILEPAY_URL} target="_blank" rel="noreferrer" className={styles.tierCta} style={style}>
                                <VolunteerActivism sx={{ fontSize: 17 }} />
                                {label}
                            </a>
                        );
                    }

                    return (
                        <div className={styles.tierCard} style={{ border, boxShadow: shadow }} id={`tier-${tier.key}`} key={tier.key}>
                            {isCurrent && (
                                <div
                                    className={styles.tierBadge}
                                    style={isTop ? { background: GOLD, color: '#1a1a1a' } : { background: '#27ae60' }}
                                >
                                    {t('premium.yourLevel')}
                                </div>
                            )}
                            <div className={styles.tierHead}>
                                <span className={styles.tierIcon} style={{ background: tier.iconBg, color: tier.iconColor }}>
                                    <TierIcon sx={{ fontSize: 22 }} />
                                </span>
                                <div className={styles.tierName}>{t(`premium.tiers.${tier.key}`)}</div>
                            </div>
                            <div className={styles.tierAmount}>
                                <span className={styles.tierAmountValue} style={{ color: tier.amountColor }}>{tier.amount}</span>
                                {tier.unit && <span className={styles.tierAmountUnit}>{tier.unit}</span>}
                            </div>
                            <div className={styles.tierPerks}>
                                {(Array.isArray(perkTexts) ? perkTexts : []).map((text, i) => {
                                    const perk = tier.perks[i] ?? { icon: CheckCircle };
                                    const PerkIcon = perk.icon;
                                    return (
                                        <div className={styles.tierPerk} key={text}>
                                            <PerkIcon sx={{ fontSize: 17, color: perk.color ?? GREEN, marginTop: '1px', flexShrink: 0 }} />
                                            <span className={styles.tierPerkText} style={{ color: perk.textColor ?? 'rgba(255,255,255,0.8)' }}>{text}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            {cta}
                        </div>
                    );
                })}
            </div>

            {/* ===== Comparison ===== */}
            <h2 className={styles.sectionTitle} style={{ fontSize: '1.4rem', marginTop: 42 }}>{t('premium.compareTitle')}</h2>
            <p className={styles.sectionDesc} style={{ marginBottom: 18 }}>{t('premium.compareDesc')}</p>
            <div className={styles.compare}>
                <div className={styles.compareScroll}>
                    <div className={styles.compareHead}>
                        <div className={styles.compareHeadBenefit}>{t('premium.benefit')}</div>
                        {[
                            { label: t('premium.tiers.donator'), color: '#4ade80', value: 25, anchor: 'tier-donator' },
                            { label: '100+', color: '#4ecbe0', value: 100, anchor: 'tier-tier100' },
                            { label: t('premium.tiers.premium'), color: '#ff9933', value: 200, anchor: 'tier-premium' },
                            { label: t('premium.compareTop'), color: GOLD, value: TOP_TIER, anchor: 'tier-top' },
                            { label: t('premium.tiers.team'), color: '#f15c25', value: TEAM_COL, anchor: 'tier-team' },
                        ].map((h) => (
                            <a
                                href={`#${h.anchor}`}
                                className={`${styles.compareHeadTier} ${isCurrentCol(h.value) ? (isKing ? styles.colCurrentTop : styles.colCurrent) : ''}`}
                                style={{ color: h.color }}
                                key={h.label}
                            >
                                {h.label}
                            </a>
                        ))}
                    </div>
                    {(t('premium.compareRows', { returnObjects: true }) as string[]).map((label, rowIdx) => (
                        <div className={`${styles.compareRow} ${rowIdx % 2 === 0 ? styles.rowEven : styles.rowOdd}`} key={label}>
                            <div className={styles.compareLabel}>{label}</div>
                            {COMPARE_CELLS[rowIdx].map((cell, colIdx) => {
                                const highlightClass = isCurrentCol(COL_VALUES[colIdx])
                                    ? (isKing ? styles.colCurrentTop : styles.colCurrent)
                                    : '';
                                return (
                                    <div className={`${styles.compareCell} ${highlightClass}`} key={colIdx}>
                                        {cell.type === 'yes' && <CheckCircle className={styles.cellYes} sx={{ fontSize: 20 }} />}
                                        {cell.type === 'no' && <span className={styles.cellNo}>–</span>}
                                        {cell.type === 'text' && <span className={styles.cellText} style={{ color: cell.color }}>{cell.value}</span>}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {/* ===== Team tier =====
                The matrix above carries a Hold column for row-by-row comparison; this banner is
                where the tier is actually explained and priced, since it is bought per team rather
                than per person. ===== */}
            <div className={styles.teamTier} id="tier-team">
                <div className={styles.teamTierHead}>
                    <span className={styles.teamTierIcon}><Groups sx={{ fontSize: 26 }} /></span>
                    <h2 className={styles.teamTierTitle}>{t('premium.teamTier.title')}</h2>
                    {coveringTeam
                        ? <span className={styles.teamTierActive}>
                            <CheckCircle sx={{ fontSize: 16 }} />
                            {t('premium.teamTier.active', { team: coveringTeam.teamName })}
                        </span>
                        : <span className={styles.teamTierPrice}>
                            {TEAM_TIER_PRICE}
                            <span className={styles.teamTierPriceUnit}>{t('premium.teamTier.priceUnit')}</span>
                        </span>}
                </div>
                <p className={styles.teamTierDesc}>{t('premium.teamTier.desc')}</p>
                <div className={styles.teamTierPerks}>
                    {(t('premium.teamTier.perks', { returnObjects: true }) as string[]).map((text) => (
                        <div className={styles.teamTierPerk} key={text}>
                            <Check sx={{ fontSize: 17, color: GREEN, marginTop: '1px', flexShrink: 0 }} />
                            <span>{text}</span>
                        </div>
                    ))}
                </div>
                <p className={styles.teamTierNote}>
                    {t('premium.teamTier.note', { count: teamTiers.length })}
                </p>
            </div>

            {/* ===== Support CTA ===== */}
            <div className={styles.cta} id="stoet">
                <div className={styles.ctaLeft}>
                    <span className={styles.ctaIcon}>
                        <CtaIcon sx={{ fontSize: 26 }} />
                    </span>
                    <div>
                        <div className={styles.ctaTitle}>{t(`premium.cta${ctaVariant}Title`)}</div>
                        <div className={styles.ctaSub}>{t(`premium.cta${ctaVariant}Sub`)}</div>
                    </div>
                </div>
                <a href={MOBILEPAY_URL} target="_blank" rel="noreferrer" className={styles.ctaButton}>
                    <Payments sx={{ fontSize: 20 }} />
                    {t(`premium.cta${ctaVariant}Button`)}
                </a>
            </div>
        </div>
    );
};

export default DonatorPage;
